<script setup>
import { motion } from "motion-v";
import { AnimatePresence } from "motion-v";
import { DatePicker as VDatePicker } from "v-calendar";
import "v-calendar/style.css";

const props = defineProps({
  placeholder: {
    type: String,
    required: false,
    default: "선택하세요",
  },
  options: {
    type: Array,
    required: true,
    default: () => [],
  },
  modelValue: {
    type: [String, Number],
    required: false,
    default: null,
  },
  type: {
    type: String,
    required: false,
    default: "drop",
  },
  valueKey: {
    type: String,
    required: false,
    default: "value",
  },
  textKey: {
    type: String,
    required: false,
    default: "text",
  },
  size: {
    type: String,
    required: false,
    default: null,
    validator: (value) => !value || ["lg", "md", "sm"].includes(value),
  },
  iconClass: {
    type: String,
    required: false,
    default: "ico-chevron-down",
  },
  link: {
    type: Boolean,
    required: false,
    default: false,
  },
  hrefKey: {
    type: String,
    required: false,
    default: "href",
  },
  targetKey: {
    type: String,
    required: false,
    default: "target",
  },
});

const emits = defineEmits(["update:modelValue", "change"]);

const isActive = ref(false);
const selectedValue = ref(props.modelValue);
const selectboxRef = ref(null);
const isDateOpen = ref(false);
const dateRange = ref({
  start: null,
  end: null,
});

// VCalendar masks 설정
const calendarMasks = {
  title: "YYYY년 M월",
  weekdays: "WW", // Mo, Tu, We 형식 (2글자)
};

// 외부 클릭 시 닫기
const handleClickOutside = (event) => {
  if (selectboxRef.value && !selectboxRef.value.contains(event.target)) {
    isActive.value = false;
    isDateOpen.value = false;
  }
};

// 버튼 클릭 시 토글
const toggleSelect = () => {
  isActive.value = !isActive.value;
  if (isActive.value) {
    nextTick(() => {
      document.addEventListener("click", handleClickOutside);
    });
  } else {
    // 셀렉트가 닫힐 때 date도 닫기
    isDateOpen.value = false;
    document.removeEventListener("click", handleClickOutside);
  }
};

// 옵션 선택
const selectOption = (option, index) => {
  // date 옵션인 경우 일반 선택 로직을 건너뛰고 date div를 표시 (셀렉트는 열어둠)
  if (option.date) {
    // date picker를 열 때만 dateRange 초기화 (이미 선택된 날짜가 없을 때만)
    if (!isDateOpen.value && (!dateRange.value || !dateRange.value.start || !dateRange.value.end)) {
      dateRange.value = {
        start: null,
        end: null,
      };
    }
    isDateOpen.value = !isDateOpen.value;
    return;
  }

  selectedValue.value = option[props.valueKey];
  emits("update:modelValue", option[props.valueKey]);
  emits("change", option[props.valueKey], index);
  isActive.value = false;
  isDateOpen.value = false;
  document.removeEventListener("click", handleClickOutside);
};

// form 타입에서 옵션 변경 처리
const handleFormChange = (event) => {
  const value = event.target.value;
  selectedValue.value = value;
  emits("update:modelValue", value);
};

// 날짜 포맷팅 함수
const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
};

// 현재 표시할 텍스트
const displayText = computed(() => {
  // dateRange가 선택된 경우 날짜 범위 표시
  if (dateRange.value && dateRange.value.start && dateRange.value.end) {
    const startDate = formatDate(dateRange.value.start);
    const endDate = formatDate(dateRange.value.end);
    return `${startDate} ~ ${endDate}`;
  }

  if (selectedValue.value) {
    let find = props.options.find(
      (option) => option[props.valueKey] === selectedValue.value
    );
    if (find) return find[props.textKey];
  }
  return props.placeholder;
});

// 컴포넌트 언마운트 시 이벤트 리스너 제거
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

// modelValue 변경 감지
watch(
  () => props.modelValue,
  (newValue) => {
    selectedValue.value = newValue;
  }
);

// dateRange 변경 감지 - start와 end가 모두 선택되면 처리
watch(
  () => dateRange.value,
  async (newRange) => {
    // newRange가 null이거나 객체가 아니면 무시
    if (!newRange || typeof newRange !== "object") {
      return;
    }

    // start와 end가 모두 있는 경우에만 처리
    if (newRange.start && newRange.end) {
      // date 옵션의 value로 설정
      const dateOption = props.options.find((option) => option.date);
      if (dateOption) {
        selectedValue.value = dateOption[props.valueKey];
        emits("update:modelValue", dateOption[props.valueKey]);
        emits(
          "change",
          dateOption[props.valueKey],
          props.options.indexOf(dateOption),
          {
            dateRange: {
              start: newRange.start,
              end: newRange.end,
            },
          }
        );
      }

      // 즉시 닫지 않고 한 프레임 대기 후 닫기
      await nextTick();
      isDateOpen.value = false;
      isActive.value = false;
      document.removeEventListener("click", handleClickOutside);
    }
  },
  { deep: true }
);

defineExpose({
  dateRange,
});
</script>

<template>
  <div class="select" ref="selectboxRef">
    <!-- drop 타입: 커스텀 드롭다운 -->
    <div v-if="props.type === 'drop'" class="selectbox selectbox--drop" :class="{
      active: isActive,
      [`selectbox--${props.size}`]: props.size,
    }">
      <button type="button" :class="['selectbox__btn', 'ico-after', props.iconClass]" @click="toggleSelect">
        {{ displayText }}
      </button>
      <AnimatePresence>
        <motion.ul v-if="isActive" key="select-list" class="selectbox__list" :initial="{
          opacity: 0,
          y: -10,
        }" :animate="{
            opacity: 1,
            y: 0,
            transition: {
              type: 'spring',
              stiffness: 300,
              damping: 20,
              mass: 0.5,
            },
          }" :exit="{
            opacity: 0,
            y: -10,
            transition: {
              duration: 0.15,
              ease: [0.4, 0, 0.2, 1],
            },
          }">
          <li v-for="(option, index) in options" :key="index" class="selectbox__item ico-after" :class="{
            active: selectedValue === option[props.valueKey],
            'ico-check': selectedValue === option[props.valueKey],
          }" role="option" @click="selectOption(option, index)">
            <a v-if="props.link && option[props.hrefKey]" :href="option[props.hrefKey]"
              :target="option[props.targetKey] || '_self'" class="selectbox__link" @click.stop>
              {{ option[props.textKey] }}
            </a>
            <span v-else>
              <i v-if="option.date" class="ico-date"></i>
              <template v-if="
                option.date && dateRange && dateRange.start && dateRange.end
              ">
                {{ formatDate(dateRange.start) }} ~
                {{ formatDate(dateRange.end) }}
              </template>
              <template v-else-if="option.date && (!dateRange || !dateRange.start || !dateRange.end)">
                날짜 선택
              </template>
              <template v-else-if="!option.date">{{
                option[props.textKey]
              }}</template>
            </span>
          </li>
        </motion.ul>

        <Transition name="select-date" enter-active-class="select-date-enter-active"
          leave-active-class="select-date-leave-active" enter-from-class="select-date-enter-from"
          leave-to-class="select-date-leave-to">
          <div v-show="isDateOpen" key="select-date" class="selectbox__date">
            <ClientOnly>
              <VDatePicker v-model.range="dateRange" mode="date" :is-required="false" locale="en"
                :masks="calendarMasks" />
            </ClientOnly>
          </div>
        </Transition>
      </AnimatePresence>
    </div>

    <!-- form 타입: 일반 select 태그 -->
    <div v-else-if="props.type === 'form'" class="selectbox selectbox--form" :class="{
      [`selectbox--${props.size}`]: props.size,
    }">
      <select class="selectbox__select" :value="selectedValue" @input="handleFormChange">
        <option hidden :selected="!selectedValue" :value="null">
          {{ placeholder }}
        </option>
        <option v-for="(option, index) in options" :key="index" :value="option[props.valueKey]">
          {{ option[props.textKey] }}
        </option>
      </select>
    </div>
  </div>
</template>

<style scoped>
/* 날짜 선택기 트랜지션 */
.select-date-enter-active,
.select-date-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.select-date-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.select-date-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
